import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  SpeedFeeCreateFormSchema,
  speedFeeSchema,
  useUpdateFee,
} from "@/api/pricings";
import { SpeedFee } from "@/api.types";
import { toast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCustomerTiers } from "@/api/customers";
import { useState, useEffect } from "react";
import { useDepots } from "@/api/depots";
import { useServiceAreaByDepotId } from "@/api/service-areas";

interface EditSpeedFeeFormProps {
  fee: SpeedFee;
  onClose: () => void;
}

export function EditSpeedFeeForm({ fee, onClose }: EditSpeedFeeFormProps) {
  const { data: customerTiersResponse } = useCustomerTiers();
  const customerTiers = customerTiersResponse?.data ?? [];

  const [selectedDepot, setSelectedDepot] = useState<string | null>(null);
  const { data: depots = [] } = useDepots();
  const { data: serviceAreasResponse } = useServiceAreaByDepotId(selectedDepot);
  const safeServiceAreas = serviceAreasResponse?.data ?? [];

  // Find parent depot for existing service areas if they exist
  useEffect(() => {
    if (fee.pickupServiceArea && !selectedDepot) {
      // Try to find the depot that contains this service area
      const depot = depots.find((d) => d.id === fee.pickupServiceArea?.depotId);
      if (depot) {
        setSelectedDepot(depot.id);
      }
    } else if (fee.deliveryServiceArea && !selectedDepot) {
      const depot = depots.find(
        (d) => d.id === fee.deliveryServiceArea?.depotId
      );
      if (depot) {
        setSelectedDepot(depot.id);
      }
    }
  }, [fee, depots, selectedDepot]);

  const form = useForm<SpeedFeeCreateFormSchema>({
    resolver: zodResolver(speedFeeSchema),
    values: {
      name: fee.name,
      type: fee.type,
      fee: fee.fee,
      feeScope: fee.feeScope,
      description: fee.description ?? "",
      customerTierId: fee.customerTier?.id ?? "",
      isActive: fee.isActive,
      minDeliveryTime: fee.minDeliveryTime ?? undefined,
      maxDeliveryTime: fee.maxDeliveryTime ?? undefined,
      timeUnit: fee.timeUnit ?? undefined,
      pickupServiceAreaId: fee.pickupServiceArea
        ? fee.pickupServiceArea.id
        : "none",
      deliveryServiceAreaId: fee.deliveryServiceArea
        ? fee.deliveryServiceArea.id
        : "none",
    },
  });

  const updateFee = useUpdateFee("speed", {
    onSuccess: () => {
      toast({
        title: "Fee updated successfully",
        description: "The fee has been updated successfully.",
      });
      onClose();
    },
    onError: () => {
      toast({
        title: "Failed to update fee",
        description: "The fee could not be updated.",
      });
    },
  });

  const onSubmit = async (data: SpeedFeeCreateFormSchema) => {
    const submissionData = {
      ...data,
      pickupServiceAreaId:
        data.pickupServiceAreaId === "none" ? "" : data.pickupServiceAreaId,
      deliveryServiceAreaId:
        data.deliveryServiceAreaId === "none" ? "" : data.deliveryServiceAreaId,
    };

    await updateFee.mutateAsync({
      id: fee.id,
      data: submissionData,
    });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Speed Fee</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="STANDARD">Standard</SelectItem>
                      <SelectItem value="EXPRESS">Express</SelectItem>
                      <SelectItem value="SAME_DAY">Same Day</SelectItem>
                      <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g., Delivery in 3-5 business days"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="minDeliveryTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Min Delivery Time</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const value = e.target.value
                            ? Number(e.target.value)
                            : undefined;
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="maxDeliveryTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Delivery Time</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const value = e.target.value
                            ? Number(e.target.value)
                            : undefined;
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="timeUnit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Time Unit</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ""}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select time unit" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="HOURS">Hours</SelectItem>
                        <SelectItem value="DAYS">Days</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="fee"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fee</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="feeScope"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Scope</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select scope" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="DOMESTIC">Domestic</SelectItem>
                      <SelectItem value="INTERNATIONAL">
                        International
                      </SelectItem>
                      <SelectItem value="ANY">Any</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customerTierId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer Tier</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select customer tier" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {customerTiers.map((tier) => (
                        <SelectItem key={tier.id} value={tier.id}>
                          {tier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormLabel>Service Areas</FormLabel>
            <Select
              onValueChange={setSelectedDepot}
              value={selectedDepot ?? undefined}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select depot" />
              </SelectTrigger>
              <SelectContent>
                {depots?.map((depot) => (
                  <SelectItem key={depot.id} value={depot.id}>
                    {depot.name} (#{depot.depotNumber})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedDepot && (
              <>
                <FormField
                  control={form.control}
                  name="pickupServiceAreaId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pickup Service Area</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select pickup area" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">
                            None (Global Fee)
                          </SelectItem>
                          {safeServiceAreas.map((area) => (
                            <SelectItem key={area.id} value={area.id}>
                              {area.name} ({area.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="deliveryServiceAreaId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Delivery Service Area</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select delivery area" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">
                            None (Global Fee)
                          </SelectItem>
                          {safeServiceAreas.map((area) => (
                            <SelectItem key={area.id} value={area.id}>
                              {area.name} ({area.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel>Active</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateFee.isPending}>
                {updateFee.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
